import { useState } from "react";
import { Monitor, CheckCircle2, XCircle } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { PageLoader } from "@/components/ui/Spinner";
import {
  useScheduleEntries,
  useApproveScheduleEntry,
  useRejectScheduleEntry,
} from "../hooks/useSchedule";
import type { WorkScheduleEntry } from "../types";
import { countWorkingDays } from "@/utils/workingDays";
import { formatDate } from "@/utils/formatDate";
import { RemoteWorkResolvedTable } from "../components/RemoteWorkResolvedTable";

export function RemoteWorkReviewPage() {
  const user = useAuthStore((s) => s.user);
  const { data: allEntries = [], isLoading } = useScheduleEntries();

  const pendingList = allEntries.filter(
    (e) => e.entry_type === "remote" && e.status === "pending"
  );
  const resolvedList = allEntries.filter(
    (e) =>
      e.entry_type === "remote" &&
      (e.status === "approved" || e.status === "rejected")
  );

  const approveEntry = useApproveScheduleEntry();
  const rejectEntry = useRejectScheduleEntry();

  const [approveModal, setApproveModal] = useState<WorkScheduleEntry | null>(null);
  const [approveComment, setApproveComment] = useState("");
  const [approveError, setApproveError] = useState("");
  const [rejectModal, setRejectModal] = useState<WorkScheduleEntry | null>(null);
  const [rejectComment, setRejectComment] = useState("");
  const [rejectError, setRejectError] = useState("");

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Praca zdalna — do zatwierdzenia
        </h1>
        <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-3" />
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Wnioski pracowników o pracę zdalną oczekujące na decyzję.
        </p>
        <p className="text-xs text-[var(--color-primary)] font-medium mt-0.5">
          {user?.is_ceo || user?.role === "hr" || user?.role === "accountant"
            ? "Wszystkie oddziały"
            : user?.branch?.name
            ? `Oddział: ${user.branch.name}`
            : null}
        </p>
      </div>

      {isLoading ? (
        <PageLoader />
      ) : pendingList.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 p-8 text-center max-w-4xl">
          <Monitor size={32} className="text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-gray-400 dark:text-gray-500 text-sm">Brak oczekujących wniosków o pracę zdalną.</p>
        </div>
      ) : (
        // Pending cards stay narrow: a one-line request stretched to a wide screen reads badly.
        <div className="flex flex-col gap-2 max-w-4xl" data-testid="remote-review-list">
          {pendingList.map((entry) => (
            <div key={entry.id} className="bg-white dark:bg-gray-800 flex items-center gap-3 px-4 py-4">
              <div className="flex-1 min-w-0">
                <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                  {entry.employee_name}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                  Praca zdalna · {formatDate(entry.start_date)}
                  {entry.start_date !== entry.end_date && ` – ${formatDate(entry.end_date)}`}
                  {entry.start_date !== entry.end_date && (
                    <span className="ml-1">
                      ({countWorkingDays(entry.start_date, entry.end_date)} dni rob.)
                    </span>
                  )}
                </span>
                <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">
                  · złożono {new Date(entry.created_at).toLocaleString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </span>
                {entry.note && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 italic">{entry.note}</p>
                )}
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={() => { setApproveComment(""); setApproveError(""); setApproveModal(entry); }}
                  className="flex items-center gap-1 text-xs bg-green-600 text-white px-2.5 py-1.5 rounded-md hover:bg-green-700 font-medium"
                >
                  <CheckCircle2 size={12} /> Zatwierdź
                </button>
                <button
                  onClick={() => { setRejectComment(""); setRejectError(""); setRejectModal(entry); }}
                  className="flex items-center gap-1 text-xs border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 px-2.5 py-1.5 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 font-medium"
                >
                  <XCircle size={12} /> Odrzuć
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Zestawienie rozwiązanych wniosków ── */}
      <div className="mt-10">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Zestawienie wniosków
        </h2>
        <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-3" />
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Historia zatwierdzonych i odrzuconych wniosków o pracę zdalną.
        </p>
        <RemoteWorkResolvedTable entries={resolvedList} showEmployee />
      </div>

      <Modal open={!!approveModal} onClose={() => setApproveModal(null)} title="Zatwierdź pracę zdalną">
        <div className="flex flex-col gap-4">
          {approveModal && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-sm">
              <p className="font-medium text-green-800 dark:text-green-300">{approveModal.employee_name}</p>
              <p className="text-xs text-green-700 dark:text-green-400 mt-0.5">
                {formatDate(approveModal.start_date)}
                {approveModal.start_date !== approveModal.end_date && ` – ${formatDate(approveModal.end_date)}`}
              </p>
              {approveModal.note && (
                <p className="text-xs text-green-700 dark:text-green-400 mt-1.5 italic border-t border-green-200 dark:border-green-700 pt-1.5">
                  „{approveModal.note}"
                </p>
              )}
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Komentarz <span className="text-gray-400 text-xs">(opcjonalny)</span>
            </label>
            <textarea
              rows={2}
              value={approveComment}
              onChange={(e) => setApproveComment(e.target.value)}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
            />
          </div>
          {approveError && <p className="text-sm text-red-600">{approveError}</p>}
          <div className="flex gap-3">
            <Button
              loading={approveEntry.isPending}
              onClick={() => {
                if (!approveModal) return;
                setApproveError("");
                approveEntry.mutate(
                  { id: approveModal.id, comment: approveComment },
                  {
                    onSuccess: () => setApproveModal(null),
                    onError: (err: unknown) => {
                      const detail = (err as { response?: { data?: { errors?: { detail?: string } } } })
                        ?.response?.data?.errors?.detail;
                      setApproveError(detail ?? "Błąd podczas zatwierdzania.");
                    },
                  }
                );
              }}
            >
              <CheckCircle2 size={14} className="mr-1.5" /> Zatwierdź
            </Button>
            <Button variant="secondary" onClick={() => setApproveModal(null)}>Anuluj</Button>
          </div>
        </div>
      </Modal>

      <Modal open={!!rejectModal} onClose={() => setRejectModal(null)} title="Odrzuć wniosek">
        <div className="flex flex-col gap-4">
          {rejectModal && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm">
              <p className="font-medium text-red-800 dark:text-red-300">{rejectModal.employee_name}</p>
              <p className="text-xs text-red-700 dark:text-red-400 mt-0.5">
                {formatDate(rejectModal.start_date)}
                {rejectModal.start_date !== rejectModal.end_date && ` – ${formatDate(rejectModal.end_date)}`}
              </p>
              {rejectModal.note && (
                <p className="text-xs text-red-700 dark:text-red-400 mt-1.5 italic border-t border-red-200 dark:border-red-700 pt-1.5">
                  „{rejectModal.note}"
                </p>
              )}
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Powód odrzucenia <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={2}
              value={rejectComment}
              onChange={(e) => setRejectComment(e.target.value)}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
            />
            {rejectError && <p className="text-xs text-red-600 mt-1">{rejectError}</p>}
          </div>
          <div className="flex gap-3">
            <Button
              variant="secondary"
              loading={rejectEntry.isPending}
              onClick={() => {
                if (!rejectComment.trim()) { setRejectError("Komentarz jest wymagany."); return; }
                if (!rejectModal) return;
                rejectEntry.mutate(
                  { id: rejectModal.id, comment: rejectComment },
                  { onSuccess: () => setRejectModal(null) }
                );
              }}
            >
              <XCircle size={14} className="mr-1.5" /> Odrzuć
            </Button>
            <Button variant="secondary" onClick={() => setRejectModal(null)}>Anuluj</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
